/**
 * cart-drawer-features.js
 * Powers the Moon-style cart drawer additions:
 *  - Frequently Bought Together / upsell slider (pulled from Shopify product recommendations)
 *  - Discount code apply field
 *  - Shipping insurance toggle
 *  - Quick view popup (title/image click on FBT items and shipping insurance)
 *
 * The free shipping / free gift / discount progress bar and the banner are pure Liquid
 * (server-rendered) and need no JS — they simply refresh whenever the drawer section re-renders.
 */

(function () {
  'use strict';

  /* -------------------------------------------------------------------- */
  /*  Helpers                                                              */
  /* -------------------------------------------------------------------- */

  function formatMoney(cents, format) {
    format = format || (window.cartDrawerMoneyFormat || '${{amount}}');
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format;

    function defaultOption(opts, name, fallback) {
      return typeof opts[name] === 'undefined' ? fallback : opts[name];
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption({ precision: precision }, 'precision', 2);
      thousands = defaultOption({ thousands: thousands }, 'thousands', ',');
      decimal = defaultOption({ decimal: decimal }, 'decimal', '.');

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';

      return dollars + cents2;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function getRoutes() {
    return (
      window.routes || {
        cart_add_url: '/cart/add',
        cart_change_url: '/cart/change.js',
        cart_url: '/cart',
      }
    );
  }

  /* -------------------------------------------------------------------- */
  /*  Central "refresh the whole drawer" helper                            */
  /*  Any time we add/change something from outside the native cart-items */
  /*  flow (upsell add, insurance toggle) we re-fetch the cart-drawer      */
  /*  section HTML and swap it in, then (re)open the drawer.               */
  /* -------------------------------------------------------------------- */

  function refreshCartDrawer(openDrawer) {
    var routes = getRoutes();
    return fetch(routes.cart_url + '?section_id=cart-drawer')
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newInner = doc.querySelector('.drawer__inner');
        var currentInner = document.querySelector('#CartDrawer .drawer__inner');
        if (newInner && currentInner) {
          currentInner.replaceWith(newInner);
        }
        var newBubble = doc.querySelector('#cart-icon-bubble');
        var currentBubble = document.querySelector('#cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.replaceWith(newBubble);
        }
        if (openDrawer) {
          var cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }
        initUpsellSliders();
        initCartTimer();
        return true;
      });
  }

  function addVariantToCart(variantId, quantity) {
    var routes = getRoutes();
    return fetch(routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    }).then(function (res) {
      return res.json();
    });
  }

  function changeCartLine(variantId, quantity) {
    var routes = getRoutes();
    return fetch(routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    }).then(function (res) {
      return res.json();
    });
  }

  /* -------------------------------------------------------------------- */
  /*  Discount code field                                                  */
  /*  Applied via a hidden iframe hitting /discount/CODE so the visible    */
  /*  page never navigates/reloads, then the drawer is refreshed via AJAX. */
  /* -------------------------------------------------------------------- */

  function applyDiscountViaIframe(code) {
    return new Promise(function (resolve) {
      var iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');

      var resolved = false;
      function finish() {
        if (resolved) return;
        resolved = true;
        setTimeout(function () {
          iframe.remove();
        }, 50);
        resolve();
      }

      iframe.addEventListener('load', finish);
      // Fallback in case the load event doesn't fire reliably (slow network, blocked, etc.)
      setTimeout(finish, 3000);

      // Redirecting to /cart inside the hidden iframe applies the discount to the session
      // without ever navigating the visible page.
      iframe.src = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart');
      document.body.appendChild(iframe);
    });
  }

  function wasDiscountApplied(code) {
    return fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var codeLower = code.toLowerCase();
        var cartLevelMatch = (cart.cart_level_discount_applications || []).some(function (d) {
          return (d.title || '').toLowerCase() === codeLower;
        });
        var lineLevelMatch = (cart.items || []).some(function (item) {
          return (item.line_level_discount_allocations || []).some(function (d) {
            return (d.discount_application.title || '').toLowerCase() === codeLower;
          });
        });
        return cartLevelMatch || lineLevelMatch;
      })
      .catch(function () {
        return null; // Unknown - couldn't verify either way.
      });
  }

  function initDiscountField() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-apply-discount]');
      if (!button) return;
      var wrap = button.closest('[data-cart-drawer-discount]');
      var input = wrap.querySelector('.cart-drawer-discount__input');
      var message = wrap.parentElement.querySelector('[data-discount-message]');
      var code = (input.value || '').trim();

      // Empty input: do nothing at all, matching Moon's behaviour.
      if (!code) {
        return;
      }

      var validCodes = window.cartDrawerValidDiscountCodes || [];
      if (validCodes.length) {
        var codeLower = code.toLowerCase();
        var isWhitelisted = validCodes.some(function (validCode) {
          return validCode.toLowerCase() === codeLower;
        });
        if (!isWhitelisted) {
          if (message) {
            message.hidden = false;
            message.textContent = 'That discount code is invalid.';
          }
          return;
        }
      }

      button.disabled = true;
      button.textContent = 'Applying…';
      if (message) message.hidden = true;

      applyDiscountViaIframe(code)
        .then(function () {
          return refreshCartDrawer(false);
        })
        .then(function () {
          return wasDiscountApplied(code);
        })
        .then(function (applied) {
          if (message) {
            message.hidden = false;
            if (applied === true) {
              message.textContent = 'Discount applied!';
              input.value = '';
            } else if (applied === false) {
              message.textContent = 'That discount code is invalid.';
            } else {
              message.textContent = 'Applied - refresh to confirm.';
            }
          }
        })
        .finally(function () {
          button.disabled = false;
          button.textContent = 'Apply';
        });
    });
  }

  /* -------------------------------------------------------------------- */
  /*  Shipping insurance toggle                                            */
  /* -------------------------------------------------------------------- */

  function initShippingInsurance() {
    document.addEventListener('change', function (event) {
      var toggle = event.target.closest('[data-insurance-toggle]');
      if (!toggle) return;

      var row = toggle.closest('[data-cart-drawer-insurance]');
      var variantId = row.getAttribute('data-variant-id');
      toggle.disabled = true;

      var request = toggle.checked ? addVariantToCart(variantId, 1) : changeCartLine(variantId, 0);

      request
        .then(function () {
          return refreshCartDrawer(false);
        })
        .catch(function () {
          toggle.checked = !toggle.checked;
        })
        .finally(function () {
          toggle.disabled = false;
        });
    });
  }

  /* -------------------------------------------------------------------- */
  /*  Frequently Bought Together / Upsell slider                          */
  /* -------------------------------------------------------------------- */

  function buildUpsellCard(product, enableQuickview) {
    var li = document.createElement('li');
    li.className = 'cart-drawer-upsell__card';

    var variant = product.variants && product.variants[0];
    var priceHtml = variant ? formatMoney(variant.price) : '';
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var quickviewAttrs = enableQuickview
      ? ' data-quickview-trigger data-product-handle="' + product.handle + '" role="button" tabindex="0"'
      : '';

    li.innerHTML =
      '<div class="cart-drawer-upsell__card-media"' +
      quickviewAttrs +
      '>' +
      (image ? '<img src="' + image + '&width=200" alt="' + (product.title || '').replace(/"/g, '&quot;') + '" loading="lazy">' : '') +
      '</div>' +
      '<div class="cart-drawer-upsell__card-info">' +
      '<p class="cart-drawer-upsell__card-title"' +
      quickviewAttrs +
      '>' +
      (product.title || '') +
      '</p>' +
      '<p class="cart-drawer-upsell__card-price">' +
      priceHtml +
      '</p>' +
      '</div>' +
      '<button type="button" class="cart-drawer-upsell__card-add" data-add-variant="' +
      (variant ? variant.id : '') +
      '">Add</button>';

    return li;
  }

  function populateUpsellSlider(container) {
    if (!container || container.dataset.loaded === 'true') return;

    var limit = parseInt(container.getAttribute('data-limit'), 10) || 8;
    var cartProductIds = (container.getAttribute('data-cart-product-ids') || '')
      .split(',')
      .filter(Boolean);
    var enableQuickview = container.getAttribute('data-quickview-enabled') === 'true';
    var track = container.querySelector('[data-upsell-track]');

    if (!cartProductIds.length) return;

    container.dataset.loaded = 'true';

    var fetches = cartProductIds.map(function (id) {
      return fetch('/recommendations/products.json?product_id=' + id + '&limit=6&intent=complementary')
        .then(function (res) {
          return res.ok ? res.json() : { products: [] };
        })
        .catch(function () {
          return { products: [] };
        });
    });

    Promise.all(fetches).then(function (results) {
      var seen = {};
      var products = [];

      results.forEach(function (result) {
        (result.products || []).forEach(function (product) {
          var idStr = String(product.id);
          if (seen[idStr]) return;
          if (cartProductIds.indexOf(idStr) !== -1) return;
          seen[idStr] = true;
          products.push(product);
        });
      });

      products = products.slice(0, limit);

      if (!products.length) {
        container.hidden = true;
        return;
      }

      track.innerHTML = '';
      products.forEach(function (product) {
        track.appendChild(buildUpsellCard(product, enableQuickview));
      });

      container.hidden = false;
      updateSliderNav(container);
    });
  }

  function updateSliderNav(container) {
    var track = container.querySelector('[data-upsell-track]');
    var countLabel = container.querySelector('[data-upsell-count]');
    var prevBtn = container.querySelector('.cart-drawer-upsell__prev');
    var nextBtn = container.querySelector('.cart-drawer-upsell__next');
    if (!track) return;

    var cards = track.children.length;
    var visible = Math.max(1, Math.round(track.clientWidth / (track.firstElementChild ? track.firstElementChild.offsetWidth + 12 : 160)));
    var pages = Math.max(1, Math.ceil(cards / visible));
    var currentPage = Math.round(track.scrollLeft / track.clientWidth) + 1;

    if (countLabel) countLabel.textContent = currentPage + '/' + pages;
    if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
    if (nextBtn) nextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
  }

  function initUpsellSliders() {
    document.querySelectorAll('[data-cart-drawer-upsell]').forEach(function (container) {
      populateUpsellSlider(container);
    });
  }

  function initUpsellEvents() {
    document.addEventListener('click', function (event) {
      var nextBtn = event.target.closest('.cart-drawer-upsell__next');
      var prevBtn = event.target.closest('.cart-drawer-upsell__prev');
      if (nextBtn || prevBtn) {
        var container = event.target.closest('[data-cart-drawer-upsell]');
        var track = container.querySelector('[data-upsell-track]');
        track.scrollBy({ left: nextBtn ? track.clientWidth : -track.clientWidth, behavior: 'smooth' });
        setTimeout(function () {
          updateSliderNav(container);
        }, 350);
        return;
      }

      var addBtn = event.target.closest('[data-add-variant]');
      if (addBtn) {
        var variantId = addBtn.getAttribute('data-add-variant');
        if (!variantId) return;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';
        addVariantToCart(variantId, 1)
          .then(function () {
            return refreshCartDrawer(true);
          })
          .catch(function () {
            addBtn.disabled = false;
            addBtn.textContent = 'Add';
          });
      }
    });

    document.addEventListener(
      'scroll',
      function (event) {
        var track = event.target.closest && event.target.closest('[data-upsell-track]');
        if (!track) return;
        var container = track.closest('[data-cart-drawer-upsell]');
        updateSliderNav(container);
      },
      true
    );
  }

  /* -------------------------------------------------------------------- */
  /*  Quick view popup (FBT items + shipping insurance)                    */
  /* -------------------------------------------------------------------- */


  var quickViewRequestId = 0;

  function openQuickView(handle) {
    var modal = document.querySelector('[data-quickview-modal]');
    if (!modal) return;

    var requestId = ++quickViewRequestId;

    var loading = modal.querySelector('[data-quickview-loading]');
    var content = modal.querySelector('[data-quickview-content]');

    modal.hidden = false;
    document.body.classList.add('overflow-hidden');
    loading.textContent = 'Loading…';
    loading.hidden = false;
    loading.classList.remove('hidme');
    content.hidden = true;

    fetch('/products/' + handle + '.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (product) {
        
     
        // A newer quickview request has started since this one began - ignore this
        // stale result so it can never overwrite/mix with what's currently showing.
        if (requestId !== quickViewRequestId) return;

           loading.hidden = true;
        loading.classList.add('hidme');
        content.hidden = false;

        var variant = product.variants && product.variants[0];
        modal.querySelector('[data-quickview-image]').src = product.featured_image
          ? product.featured_image + '&width=600'
          : '';
        modal.querySelector('[data-quickview-image]').alt = product.title || '';
        modal.querySelector('[data-quickview-title]').textContent = product.title || '';
        modal.querySelector('[data-quickview-price]').textContent = variant ? formatMoney(variant.price) : '';

        var descriptionEl = modal.querySelector('[data-quickview-description]');
        var tmp = document.createElement('div');
        tmp.innerHTML = product.description || '';
        descriptionEl.textContent = (tmp.textContent || tmp.innerText || '').trim().slice(0, 400);

        var link = modal.querySelector('[data-quickview-link]');
        link.href = product.url || '#';

        
      })
      .catch(function () {
      //  if (requestId !== quickViewRequestId) return;
       // loading.textContent = 'Sorry, this product could not be loaded.';
       // loading.hidden = false;
       // loading.classList.remove('hidme');
       // content.hidden = true;
      });
  }

  function closeQuickView() {
    var modal = document.querySelector('[data-quickview-modal]');
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
  }

  function initQuickView() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-quickview-trigger]');
      if (trigger) {
        event.preventDefault();
        openQuickView(trigger.getAttribute('data-product-handle'));
        return;
      }
      if (event.target.closest('[data-quickview-close]')) {
        closeQuickView();
      }
    });

    document.addEventListener('keydown', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-quickview-trigger]');
      if (trigger && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        openQuickView(trigger.getAttribute('data-product-handle'));
      }
      if (event.key === 'Escape') {
        closeQuickView();
      }
    });
  }

    /* -------------------------------------------------------------------- */
  /*  Countdown timer                                                      */
  /*  Session-based: starts on first view, persists via sessionStorage    */
  /*  across page loads/drawer re-renders until it expires or tab closes. */
  /* -------------------------------------------------------------------- */

  var timerInterval = null;

  function formatTimer(totalSeconds) {
    var minutes = Math.floor(totalSeconds / 60);
    var seconds = totalSeconds % 60;
    return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  }

  function startCartTimer(container) {
    if (!container) return;

    var minutes = parseInt(container.getAttribute('data-minutes'), 10) || 10;
    var template = container.getAttribute('data-text-template') || 'Your cart expires in {{timer}}';
    var textEl = container.querySelector('[data-timer-text]');
    var storageKey = 'cartDrawerTimerExpiry';

    var expiry = parseInt(sessionStorage.getItem(storageKey), 10);
    var now = Date.now();

    if (!expiry || isNaN(expiry) || expiry <= now) {
      expiry = now + minutes * 60 * 1000;
      sessionStorage.setItem(storageKey, String(expiry));
    }

    if (timerInterval) clearInterval(timerInterval);

    function tick() {
      var remainingMs = expiry - Date.now();
      if (remainingMs <= 0) {
        // Restart a fresh countdown once the previous one expires.
        expiry = Date.now() + minutes * 60 * 1000;
        sessionStorage.setItem(storageKey, String(expiry));
        remainingMs = expiry - Date.now();
      }
      var remainingSeconds = Math.ceil(remainingMs / 1000);
      if (textEl) {
        textEl.textContent = template.replace('{{timer}}', formatTimer(remainingSeconds));
      }
    }

    tick();
    timerInterval = setInterval(tick, 1000);
  }

  function initCartTimer() {
    var container = document.querySelector('[data-cart-drawer-timer]');
    if (container) {
      startCartTimer(container);
    } else if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  }

  /* -------------------------------------------------------------------- */
  /*  Auto free gifts (multi-goal progress bar)                            */
  /*  Reads the data-gift*-* attributes rendered by snippets/               */
  /*  cart-progress-bar.liquid and automatically adds/removes each gift    */
  /*  variant as the customer's cart total crosses its threshold.          */
  /*  Assumes gift products are priced $0 so they don't affect the total   */
  /*  used to evaluate the thresholds (that total excludes gift lines      */
  /*  anyway, as an extra safety net).                                     */
  /* -------------------------------------------------------------------- */

  var giftSyncInProgress = false;

  function getGiftConfigs() {
    var container = document.querySelector('[data-cart-drawer-gifts]');
    if (!container) return [];

    var configs = [];
    ['1', '2'].forEach(function (n) {
      var enabled = container.getAttribute('data-gift' + n + '-enabled') === 'true';
      var variantId = container.getAttribute('data-gift' + n + '-variant-id');
      var threshold = parseFloat(container.getAttribute('data-gift' + n + '-threshold'));
      if (enabled && variantId && variantId !== '' && !isNaN(threshold)) {
        configs.push({ variantId: String(variantId), thresholdCents: Math.round(threshold * 100) });
      }
    });
    return configs;
  }

  function syncFreeGifts() {
    if (giftSyncInProgress) return;

    var configs = getGiftConfigs();
    if (!configs.length) return;

    giftSyncInProgress = true;

    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var giftVariantIds = configs.map(function (c) {
          return c.variantId;
        });

        // Cart total excluding the gift line(s) themselves, so a gift being added/removed
        // never shifts whether it (or another gift) should be unlocked.
        var nonGiftTotal = cart.items.reduce(function (sum, item) {
          return giftVariantIds.indexOf(String(item.variant_id)) === -1 ? sum + item.line_price : sum;
        }, 0);

        var actions = configs
          .map(function (config) {
            var cartItem = cart.items.filter(function (item) {
              return String(item.variant_id) === config.variantId;
            })[0];
            var currentQty = cartItem ? cartItem.quantity : 0;
            var shouldHaveGift = nonGiftTotal >= config.thresholdCents;
            var targetQty = shouldHaveGift ? 1 : 0;
            if (currentQty === targetQty) return null;
            // cart/change.js can only change the quantity of a line that already exists in the
            // cart - it can't add a brand-new line. Use cart/add.js to add the gift the first
            // time, and cart/change.js only to remove it again afterwards.
            return { id: config.variantId, quantity: targetQty, isNew: currentQty === 0 && targetQty > 0 };
          })
          .filter(Boolean);

        if (!actions.length) {
          giftSyncInProgress = false;
          return null;
        }

        // Apply changes one at a time to avoid the cart API racing itself.
        return actions
          .reduce(function (chain, action) {
            return chain.then(function () {
              return action.isNew
                ? addVariantToCart(action.id, action.quantity)
                : changeCartLine(action.id, action.quantity);
            });
          }, Promise.resolve())
          .then(function () {
            giftSyncInProgress = false;
            return refreshCartDrawer(false);
          });
      })
      .catch(function () {
        giftSyncInProgress = false;
      });
  }

  /* -------------------------------------------------------------------- */
  /*  Init                                                                 */
  /* -------------------------------------------------------------------- */

  function init() {
    initDiscountField();
    initShippingInsurance();
    initUpsellEvents();
    initQuickView();
    initUpsellSliders();
    initCartTimer();
    syncFreeGifts();

    // Re-populate the upsell slider whenever the drawer is opened (covers the
    // case where it's opened via the header cart icon rather than our own refresh flow).
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.attributeName === 'class' && cartDrawerEl.classList.contains('active')) {
            initUpsellSliders();
            initCartTimer();
          }
        });
      });
      observer.observe(cartDrawerEl, { attributes: true });
    }

    // A MutationObserver on #CartDrawer catches every one of these cases reliably,
    // regardless of timing, instead of guessing with setTimeout after a pubsub event.
    var cartDrawerRoot = document.getElementById('CartDrawer');
    if (cartDrawerRoot) {
      var reinitDebounce = null;
      var contentObserver = new MutationObserver(function () {
        if (reinitDebounce) clearTimeout(reinitDebounce);
        reinitDebounce = setTimeout(function () {
          initCartTimer();
          // initUpsellSliders();
        }, 60);
      });
      contentObserver.observe(cartDrawerRoot, { childList: true, subtree: true });
    }

    // The built-in cart-items quantity/remove flow re-renders '.drawer__inner' on its own
    // (native Dawn behaviour) - this catches that case so the upsell slider still repopulates.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(initUpsellSliders, 100);
        setTimeout(syncFreeGifts, 150);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();